import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Plus, Target, Trash2, Users } from "lucide-react";
import { audienciasApi, type Audiencia } from "../../lib/api";
import type { Criterios } from "./criterios";
import { ConstructorCriterios } from "./ConstructorCriterios";

export function AudienciasPage() {
  const [lista, setLista] = useState<Audiencia[]>([]);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => {
    audienciasApi.listar()
      .then(setLista)
      .catch((e) => setError(e instanceof Error ? e.message : "No se pudo cargar"));
  }, []);
  useEffect(cargar, [cargar]);

  async function guardar(nombre: string, criterios: Criterios) {
    setError(null);
    try {
      await audienciasApi.crear({ nombre, tipo: "dinamica", criterios });
      setCreando(false);
      cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    }
  }

  async function eliminar(id: string) {
    await audienciasApi.eliminar(id);
    cargar();
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">
        <div className="mb-5 flex items-center justify-between">
          <Link to="/tienda" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </div>

        <header className="mb-6 flex items-end justify-between">
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm">
              <Target className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold leading-tight text-ink-800">Audiencias</h1>
              <p className="mt-0.5 text-sm text-ink-500">Segmentos de clientes por criterios o selección manual</p>
            </div>
          </div>
          {!creando && (
            <button onClick={() => setCreando(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
              <Plus className="size-4" /> Nueva audiencia
            </button>
          )}
        </header>

        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

        {creando ? (
          <div className="rounded-2xl border border-ink-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-semibold text-ink-800">Nueva audiencia dinámica</h2>
              <button onClick={() => setCreando(false)} className="text-sm text-ink-500 hover:text-ink-800">Cancelar</button>
            </div>
            <ConstructorCriterios onGuardar={guardar} />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-sm">
            {lista.length === 0 ? (
              <p className="p-8 text-center text-sm text-ink-400">Todavía no hay audiencias. Crea la primera.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-left text-xs font-medium uppercase text-ink-500">
                  <tr>
                    <th className="px-4 py-3">Nombre</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {lista.map((a) => (
                    <tr key={a.id} className="hover:bg-ink-50/60">
                      <td className="px-4 py-3 font-medium text-ink-800">{a.nombre}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
                          <Users className="size-3" /> {a.tipo === "fija" ? "Lista fija" : "Dinámica"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => audienciasApi.exportar(a.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-ink-200 px-2.5 py-1.5 text-xs text-ink-700 hover:bg-ink-50">
                            <Download className="size-3.5" /> CSV
                          </button>
                          <button onClick={() => eliminar(a.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50">
                            <Trash2 className="size-3.5" /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
